'use client';

// ─────────────────────────────────────────────────────────────────────────────
// 🛹 SQUISH SKATE TOKEN BRIDGE v6.90006
// Connects to https://squish.games/wp-json/squish-games/v1
//
// Endpoints:
//   GET  /wallet/balance                  — on game load
//   POST /tokens/spend                    — on play start
//   GET  /entitlements/check              — on outfit ownership check
//   POST /entitlements/purchase           — on outfit purchase
//   GET  /skate/config                    — on config load
//
// Guest ID:
//   Stored in localStorage as: sg_guest_id
//   Sent on every request as:  X-SG-Guest-ID: sg_xxxxx
// ─────────────────────────────────────────────────────────────────────────────

const LOG_PREFIX = '🛹 SQUISH SKATE TOKEN BRIDGE v6.90006';
const API_BASE = 'https://squish.games/wp-json/squish-games/v1';
const GUEST_ID_KEY = 'sg_guest_id';

// ── Types ────────────────────────────────────────────────────────────────────

export interface WalletBalance {
  balance: number;
  guest_id?: string;
  [key: string]: unknown;
}

export interface TokenSpendResult {
  success: boolean;
  balance?: number;
  message?: string;
  [key: string]: unknown;
}

export interface EntitlementCheckResult {
  owned: boolean;
  item_id: string;
  type: string;
  [key: string]: unknown;
}

export interface EntitlementPurchaseResult {
  success: boolean;
  balance?: number;
  message?: string;
  [key: string]: unknown;
}

export interface SkateConfig {
  [key: string]: unknown;
}

// ── Guest ID helpers ─────────────────────────────────────────────────────────

function getGuestId(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(GUEST_ID_KEY);
}

function setGuestId(id: string): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(GUEST_ID_KEY, id);
  console.log(`${LOG_PREFIX} | guest_id stored →`, id);
}

// ── Base fetch wrapper ───────────────────────────────────────────────────────

async function squishFetch<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const guestId = getGuestId();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> | undefined),
  };

  if (guestId) {
    headers['X-SG-Guest-ID'] = guestId;
    console.log(`${LOG_PREFIX} | X-SG-Guest-ID →`, guestId);
  }

  const url = `${API_BASE}${path}`;
  console.log(`${LOG_PREFIX} | ${options.method ?? 'GET'} ${url}`);

  const res = await fetch(url, { ...options, headers });

  if (!res.ok) {
    console.warn(`${LOG_PREFIX} | ⚠️ HTTP ${res.status} for ${path}`);
  }

  const data = (await res.json()) as T & { guest_id?: string };

  // Persist guest_id if returned in any response
  if (data?.guest_id) {
    setGuestId(data.guest_id);
  }

  console.log(`${LOG_PREFIX} | response ←`, data);
  return data;
}

// ── 1. GET /wallet/balance — call on game load ───────────────────────────────

export async function fetchWalletBalance(): Promise<WalletBalance | null> {
  console.log(`${LOG_PREFIX} | [1] fetchWalletBalance — game load`);
  try {
    return await squishFetch<WalletBalance>('/wallet/balance');
  } catch (err) {
    console.error(`${LOG_PREFIX} | fetchWalletBalance error:`, err);
    return null;
  }
}

// ── 2. POST /tokens/spend — call on play/run start ───────────────────────────

export async function spendTokensForRun(): Promise<TokenSpendResult | null> {
  console.log(`${LOG_PREFIX} | [2] spendTokensForRun — play start`);
  try {
    return await squishFetch<TokenSpendResult>('/tokens/spend', {
      method: 'POST',
      body: JSON.stringify({
        action: 'skateboard_use',
        item_id: 'skateboard-track',
        cost: 10,
      }),
    });
  } catch (err) {
    console.error(`${LOG_PREFIX} | spendTokensForRun error:`, err);
    return null;
  }
}

// ── 3. GET /entitlements/check — call on outfit ownership check ──────────────

export async function checkOutfitEntitlement(
  outfitId: string
): Promise<EntitlementCheckResult | null> {
  console.log(`${LOG_PREFIX} | [3] checkOutfitEntitlement — outfit_id:`, outfitId);
  try {
    return await squishFetch<EntitlementCheckResult>(
      `/entitlements/check?type=outfit&item_id=${encodeURIComponent(outfitId)}`
    );
  } catch (err) {
    console.error(`${LOG_PREFIX} | checkOutfitEntitlement error:`, err);
    return null;
  }
}

// ── 4. POST /entitlements/purchase — call on outfit purchase ─────────────────

export interface OutfitPurchaseParams {
  item_id: string;
  label: string;
  cost: number;
}

export async function purchaseOutfitEntitlement(
  params: OutfitPurchaseParams
): Promise<EntitlementPurchaseResult | null> {
  console.log(`${LOG_PREFIX} | [4] purchaseOutfitEntitlement — params:`, params);
  try {
    return await squishFetch<EntitlementPurchaseResult>('/entitlements/purchase', {
      method: 'POST',
      body: JSON.stringify({
        type: 'outfit',
        item_id: params.item_id,
        label: params.label,
        cost: params.cost,
        game_id: 'skateboard-track',
      }),
    });
  } catch (err) {
    console.error(`${LOG_PREFIX} | purchaseOutfitEntitlement error:`, err);
    return null;
  }
}

// ── 5. GET /skate/config — call on config load ───────────────────────────────

export async function fetchSkateConfig(): Promise<SkateConfig | null> {
  console.log(`${LOG_PREFIX} | [5] fetchSkateConfig — config load`);
  try {
    return await squishFetch<SkateConfig>('/skate/config');
  } catch (err) {
    console.error(`${LOG_PREFIX} | fetchSkateConfig error:`, err);
    return null;
  }
}

// ── React hook — convenience wrapper ─────────────────────────────────────────

export function useSquishTokenBridge() {
  return {
    fetchWalletBalance,
    spendTokensForRun,
    checkOutfitEntitlement,
    purchaseOutfitEntitlement,
    fetchSkateConfig,
    getGuestId,
  };
}
